// Sprint 35 item 1: the words of the mini-game flow, one place for the page and the tests. Truthful: every number comes from
// game/minigames/balance.js. The rescue is a flat refund, never a halving, and the plate says so.
import { MINI, RESCUE } from './balance.js';
import { GAMES } from './registry.js';

const VERB = { bull: 'RIDE', bear: 'DUCKED', holden: 'HELD' }; // not DODGE: in the chalk face the D and O look alike and it read as a coin's name

export const PLATE = {
  bull: { bonus: "It's a bonus round. Ride the bull to the bell.", rescue: `A rescue ride. Get to the bell to win back ${MINI.bull.refund}. The bull's angrier this time.` },
  bear: { bonus: "It's a bonus round. Dodge the bear's paws.", rescue: `A rescue round. Dodge the paws to win back ${MINI.bear.refund}.` },
  holden: { bonus: "It's a bonus round. Tap to drop each bag onto the pile.", rescue: `A rescue round. Stack six bags up to the line to win back ${MINI.holden.refund}.` },
};
// Sprint 36: a rescue ride is only offered on a streak (balance.js RESCUE). When a wrong guest call has none, the sentence line says so.
export const noRescueLine = () => `No rescue ride this time. You need ${RESCUE.minTallies} right calls in a row for one.`;
export const plateOf =(game, mode) => PLATE[game]?.[mode] ?? '';
// The word that flashes over the frame as play starts, so the game says what to do at a glance (never DODGE: see VERB)
export const ACTION = { bull: 'HOLD ON!', bear: 'DUCK!', holden: 'STACK!' };
export const actionOf = game => ACTION[game] ?? '';
// The practice room says the same without a size: nothing is paid there.
const PRACTICE_RESCUE = { bull: "A rescue ride. Get to the bell. The bull's angrier this time.", bear: 'A rescue round. Dodge the paws.', holden: 'A rescue round. Stack six bags up to the line.' };
export const practicePlateOf = (game, mode) => (mode === 'rescue' ? PRACTICE_RESCUE[game] + ' Nothing is paid here.' : plateOf(game, mode));

// The chalk step after the game (Jersey 10, like the sum): "+ 35 RIDE", "RESCUED + 50", or a smear when nothing was won.
export function chalkStep({ game, mode, points }) {
  if (!(points > 0)) return { kind: 'mini', text: '- - -', smear: true };
  return { kind: 'mini', text: mode === 'rescue' ? `RESCUED + ${points}` : `+ ${points} ${VERB[game]}`, smear: false };
}

// The result line (sentence font): how it went, in seconds, never a claim the points do not back up.
const secondsText = steps => (Math.floor((steps * 10) / 15) / 10).toFixed(1); // whole tenths, in integers

// Hold the bags counts bags, not seconds: "Tipped at bag 5. 8 points." (a bonus), "Line reached. Rescued." (the line is bag 6).
const pointsText = n => `${n} point${n === 1 ? '' : 's'}`;
function holdenLine({ reachedEnd, mode, bags = 0, tippedBag = 0, points = 0 }) {
  if (mode === 'rescue') return reachedEnd ? 'Line reached. Rescued.' : `The pile tipped at bag ${tippedBag}, so no rescue.`;
  return reachedEnd ? `Still holding. ${bags} bags.` : `The pile tipped at bag ${tippedBag}. ${pointsText(points)}.`;
}

export function resultLine({ game, steps, reachedEnd, played, reason, mode, bags, tippedBag, points }) {
  const secs = secondsText(steps);
  if (reason === 'skip') return 'Skipped. No points.';
  if (reason === 'early') return 'That was too quick to count, so no points.';
  if (!played) return "You didn't move, so no points.";
  if (game === 'holden') return holdenLine({ reachedEnd, mode, bags, tippedBag, points });
  if (reachedEnd) return game === 'bull' ? 'You rode it to the bell.' : 'Still here.';
  return game === 'bull' ? `Thrown at ${secs} s.` : `Out at ${secs} s.`;
}

// The practice room shows only the time and the clean mark, nothing at stake.
export function practiceLine({ game, steps, reachedEnd, clean, mode, bags, tippedBag }) {
  const secs = secondsText(steps);
  if (game === 'holden') {
    if (mode === 'rescue') return reachedEnd ? 'Line reached.' : `Tipped at bag ${tippedBag}.`;
    return reachedEnd ? `Still holding. ${bags} bags.${clean ? ' Clean stack.' : ''}` : `Tipped at bag ${tippedBag}.`;
  }
  const head = reachedEnd ? (game === 'bull' ? 'Rode it to the bell.' : 'Still here.') : game === 'bull' ? `Thrown at ${secs} s.` : `Out at ${secs} s.`;
  return reachedEnd && clean ? `${head} ${game === 'bull' ? 'Clean ride.' : 'No marks.'}` : head;
}

// /verify rows: the guest pick and each game played, in words. `g` and `m` are the rows verifyShift (game/seed.js) returns.
export function guestRowWords(g) {
  const title = g.how === 'pick' ? `Guaranteed guest, player ${g.player}` : g.how === 'natural' ? `Second guest, player ${g.player}` : `Capped, player ${g.player}: a third guest became a plain knock`;
  const wore = look => (look === 'holden' ? ', wore Mr Holden' : '');
  const claimed = g.how === 'capped' ? `plain knock (a ${g.claimed.game} roll on door ${g.claimed.door})` : `door ${g.claimed.door}, ${g.claimed.game}${wore(g.claimed.look)}`;
  const recomputed = g.how === 'pick' ? `picked door ${g.recomputed.door} or the next, ${g.recomputed.game}${wore(g.recomputed.look)}` : `numbered roll says ${g.recomputed.game}${wore(g.recomputed.look)}`;
  return { title, claimed, recomputed };
}
// A player's name for a game, from the registry; Mr Holden's game says who it is about.
export const gameName = id => { const n = GAMES[id]?.NAME ?? id; return id === 'holden' ? `${n} (Mr Holden)` : n; };
export const miniRowWords = m => ({ title: `${gameName(m.game)} ${m.mode}, player ${m.player}`, claimed: `${m.claimed} points`,
  recomputed: `${m.recomputed} points (input trace replayed)${m.mode === 'rescue' && m.tallies !== undefined ? `; ${m.tallies} tallies going in, a rescue needs ${RESCUE.minTallies}` : ''}` });
